import { SocketClientMessage } from '@okletsplay/core';
import { omit } from 'lodash';
import { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { matchPath, useLocation, useParams } from 'react-router-dom';
import { Route } from 'app/enums';
import { useSocketSubscriber } from 'app/hooks/socket';
import { useFetchData } from 'app/hooks/store';
import { Identifier } from 'app/interfaces';
import { TournamentEntity } from 'app/interfaces/entity';
import { EditLolTournamentFormFields, UpdateTournamentAssetsFields } from '../interfaces/forms';
import { getTournamentById, updateTournamentAssets } from '../store/current-tournament/repository';
import { currentTournamentSelector } from '../store/current-tournament/selectors';
import { updateTournament } from '../store/tournaments/repository';

export const useEditTournament = () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const { pathname } = useLocation();

  const tournament = useSelector(currentTournamentSelector.tournament);

  const isEditPage = useMemo(() => Boolean(matchPath(Route.TournamentEdit, pathname)), [pathname]);

  const actions = useMemo(
    () => (isEditPage ? [getTournamentById(id as string)] : []),
    [isEditPage, id]
  );
  const { fetch, loading } = useFetchData<TournamentEntity>(actions, { fetchOnInit: true });

  const updateAssetsHandler = useCallback(
    (payload: Partial<UpdateTournamentAssetsFields>) => {
      if (!tournament) {
        return;
      }

      dispatch(
        updateTournamentAssets({
          tournamentId: tournament.id,
          payload,
        })
      );
    },
    [dispatch, tournament]
  );

  const submitHandler = useCallback(
    (data: EditLolTournamentFormFields) => {
      const spectators = data.spectators?.map((spectator) => spectator.id);

      if (!tournament) {
        return;
      }

      dispatch(
        updateTournament({
          tournamentId: tournament.id,
          payload: {
            ...omit(data, ['gameId', 'passphrase']),
            spectators,
          },
        })
      );
    },
    [dispatch, tournament]
  );

  const subscriptionHandler = useCallback(
    (identifier: Identifier) => {
      if (identifier !== tournament.id || !isEditPage) {
        return;
      }

      void fetch(false);
    },
    [isEditPage, fetch, tournament.id]
  );

  const subscriptions = useMemo(
    () => [{ action: SocketClientMessage.Tournament, handler: subscriptionHandler }],
    [subscriptionHandler]
  );

  useSocketSubscriber(subscriptions);

  return {
    loading,
    isEditPage,
    submitHandler,
    updateAssetsHandler,
  };
};
